import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { syncClientIntakesFromSheetRows } from "@/lib/client-intakes.functions";

async function assertAdmin(supabase: any, userId: string) {
  const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (!data) throw new Error("Forbidden: admin access required");
}

function planAmountPaise(plan: string | null): { name: string; paise: number } | null {
  if (!plan) return null;
  const p = plan.toLowerCase();
  if (p.includes("refusal")) return { name: "Refusal Support", paise: 7000_00 };
  if (p.includes("full")) return { name: "Full Support", paise: 4500_00 };
  if (p.includes("basic")) return { name: "Basic Support", paise: 3000_00 };
  return null;
}

function safePhone(raw: string | null): string {
  const digits = (raw ?? "").replace(/\D/g, "");
  if (digits.length >= 6) return digits;
  return "0000000000";
}

/**
 * Syncs paid clients from the intake Google Sheet into the payments table.
 * 1. Pulls the latest intake form responses into `client_intakes`.
 * 2. For every intake row with a recognised plan, upserts a `paid` payment
 *    row keyed by `manual_intake_<email>` so the admin dashboard reflects
 *    the same client list and revenue as the spreadsheet.
 */
export const syncPaymentsFromSheet = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: claimed, error: lockError } = await supabaseAdmin.rpc("claim_admin_sync_lock", {
      _lock_name: "sheet_sync",
      _hold_seconds: 180,
    });
    if (lockError) throw new Error(lockError.message);
    if (!claimed) throw new Error("A sheet sync is already running. Please wait a moment.");

    try {
      // Step 1 - refresh intakes from the sheet without calling a nested server
      // function. Nested RPC can fail in production and duplicated work was one
      // reason repeated sync clicks caused transient site failures.
      try {
        await syncClientIntakesFromSheetRows(supabaseAdmin);
      } catch (e) {
        const code = (e as { code?: string })?.code;
        if (code === "SHEETS_RATE_LIMITED") {
          return {
            syncedAt: new Date().toISOString(),
            totalIntakes: 0,
            upserted: 0,
            skippedNoPlan: 0,
            errors: [(e as Error).message],
            rateLimited: true,
          };
        }
        throw e;
      }

      // Step 2 - read fresh intakes.
      const { data: intakes, error: readErr } = await supabaseAdmin
        .from("client_intakes")
        .select("email, full_name, whatsapp, plan, visa_date, submitted_at")
        .order("submitted_at", { ascending: false });
      if (readErr) throw new Error(readErr.message);

    // Keep only the latest intake row per email.
    const latest = new Map<string, (typeof intakes)[number]>();
    for (const row of intakes ?? []) {
      const key = row.email?.toLowerCase();
      if (!key) continue;
      if (!latest.has(key)) latest.set(key, row);
    }

    let upserted = 0;
    let skippedNoPlan = 0;
    const errors: string[] = [];

    // Build all payloads first so we can do bulk delete + upsert instead of
    // ~180 sequential round-trips (which was crashing the Cloudflare Worker
    // with subrequest / CPU limits and causing ERR_CONNECTION_CLOSED and
    // subsequent "Failed to fetch" on /admin/intake).
    const payloads: Array<Record<string, unknown>> = [];
    const orderIds: string[] = [];
    const emails: string[] = [];

    for (const row of latest.values()) {
      const plan = planAmountPaise(row.plan);
      if (!plan) {
        skippedNoPlan++;
        continue;
      }
      const email = row.email!.toLowerCase();
      const orderId = `manual_intake_${email}`;
      orderIds.push(orderId);
      emails.push(email);
      payloads.push({
        plan_name: plan.name,
        amount_paise: plan.paise,
        currency: "INR",
        buyer_name: row.full_name || "Unknown",
        buyer_phone: safePhone(row.whatsapp),
        buyer_email: email,
        interview_date: row.visa_date ?? null,
        razorpay_order_id: orderId,
        status: "paid" as const,
        notes: "Imported from intake spreadsheet",
        created_at: row.submitted_at ?? new Date().toISOString(),
      });
    }

    if (emails.length > 0) {
      // Single bulk delete of legacy manual rows for these emails, excluding
      // the canonical order ids we're about to (re-)upsert.
      const quotedIds = orderIds.map((id) => `"${id.replace(/"/g, '\\"')}"`).join(",");
      const { error: dedupErr } = await supabaseAdmin
        .from("payments")
        .delete()
        .in("buyer_email", emails)
        .like("razorpay_order_id", "manual_intake_%")
        .not("razorpay_order_id", "in", `(${quotedIds})`);
      if (dedupErr) errors.push(`dedupe: ${dedupErr.message}`);

      // Single bulk upsert.
      const { error } = await supabaseAdmin
        .from("payments")
        .upsert(payloads as never, { onConflict: "razorpay_order_id" });
      if (error) {
        errors.push(error.message);
      } else {
        upserted = payloads.length;
      }
    }

      return {
        syncedAt: new Date().toISOString(),
        totalIntakes: latest.size,
        upserted,
        skippedNoPlan,
        errors: errors.slice(0, 5),
      };
    } finally {
      await supabaseAdmin.rpc("release_admin_sync_lock", { _lock_name: "sheet_sync" });
    }
  });
